import type {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import qs from 'qs'

import { QS_CONFIG } from '../../constants/api'
import type { ApiResponse } from '../../types/api'
import { getPublicAuth } from '../getPublicAuth'
import { decryptUser } from '../user'
import type { RequestPath } from '.'
import { api } from '.'

// Marks a config that already went through one refresh-and-retry round so a
// still-401 retry rejects instead of looping through the interceptor forever.
type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean }

// Single-flight slot, keyed by account: concurrent 401s for the SAME account
// (e.g. several queries firing right when the token expires) share one link.php
// refresh instead of stampeding it. Keying by account means an in-place account
// switch mid-refresh can't hand account B a request retried with account A's
// freshly minted token.
let inflightRefresh: {
  account: string
  promise: Promise<string | null>
} | null = null

async function fetchUpdatedAuth(
  user: NonNullable<ReturnType<typeof decryptUser>>,
): Promise<string | null> {
  // If the auth source itself is down the refresh cannot succeed (link.php
  // needs a valid public auth), so fall through to the original 401 instead
  // of replacing it with a foreign getPublicAuth rejection. The caller then
  // handles the error it already had, and a later request retries afresh.
  const publicAuth = await getPublicAuth().catch(() => null)
  if (!publicAuth) return null

  try {
    const updatedTokenResponse = await api.post<
      ApiResponse<{ auth: string; expired_time: number } | string>
    >(
      '/link.php',
      {
        ...user,
        auth: publicAuth,
      },
      // Mark the refresh call itself as already-retried. If /link.php answers
      // 401, its response re-enters this same interceptor; without this flag
      // the nested call would await the still-pending inflightRefresh — its
      // own promise — and deadlock every request for the rest of the session.
      // Flagged, that nested 401 rejects like any exhausted request instead.
      { _authRetried: true } as AxiosRequestConfig<string>,
    )
    const { status, info } = updatedTokenResponse.data

    if (status === 1 && typeof info !== 'string') {
      return info.auth
    }
  } catch {
    // A failed refresh call (network drop, 5xx) must not replace the original
    // 401 — the caller keeps the error it already had.
  }

  return null
}

export async function refreshAuthToken(error: AxiosError<unknown>) {
  const user = decryptUser()
  const originalConfig = error.response?.config as RetriableConfig | undefined

  if (
    error.response &&
    error.response.status === 401 &&
    user &&
    originalConfig &&
    !originalConfig._authRetried
  ) {
    if (!inflightRefresh || inflightRefresh.account !== user.username) {
      const promise = fetchUpdatedAuth(user).finally(() => {
        // Only clear the slot if a newer account's refresh hasn't replaced it.
        if (inflightRefresh?.promise === promise) inflightRefresh = null
      })
      inflightRefresh = { account: user.username, promise }
    }
    const updatedAuth = await inflightRefresh.promise

    if (updatedAuth) {
      localStorage.setItem('privateAuth', updatedAuth)

      const path = originalConfig.url as RequestPath
      originalConfig._authRetried = true
      const requestData = originalConfig.data as FormData | string
      if (requestData instanceof FormData) {
        requestData.append('auth', updatedAuth)
        const responseWithUpdatedAuth = await api.http(path, {
          ...originalConfig,
          data: requestData,
        })

        return responseWithUpdatedAuth
      } else {
        const parsedRequestData = qs.parse(requestData)
        parsedRequestData.auth = updatedAuth

        const responseWithUpdatedAuth = await api.http(path, {
          ...originalConfig,
          data: qs.stringify(parsedRequestData, QS_CONFIG),
        })

        return responseWithUpdatedAuth
      }
    }
  }

  // Rejecting (instead of returning the error) keeps the axios promise
  // rejected, so `await api.*` throws on failure rather than resolving with
  // an AxiosError whose `.data` is undefined.
  return Promise.reject(error)
}
